import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Download, Send } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';

// Step-by-step setup for the Microsoft Teams plugin, with the actions each step needs.
const TeamsSetupDialog = ({ integration, onClose, onManageLinks, onChanged }) => {
  const [setup, setSetup] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/integrations/${integration.id}/teams-setup`);
      setSetup(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load Teams setup');
    }
  }, [integration.id]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, request, success) => {
    setBusy(key);
    try { await request(); if (success) toast.success(success); await load(); onChanged?.(); }
    catch (error) { toast.error(error.response?.data?.detail || 'That did not work. Try again.'); }
    finally { setBusy(''); }
  };
  const downloadPackage = () => run('package', async () => {
    const response = await axiosInstance.get('/admin/integrations/teams-app-package', { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url; link.download = 'qflow-teams-app.zip'; link.click();
    URL.revokeObjectURL(url);
  }, 'Teams app package downloaded');
  const setAutoLink = (checked) => run('autolink', () => axiosInstance.put(`/admin/integrations/${integration.id}`, {
    config: { ...(integration.config || {}), auto_link_by_email: checked },
  }), checked ? 'People will be linked by work email' : 'Automatic linking turned off');
  const sendTest = () => run('test', () => axiosInstance.post(`/admin/integrations/${integration.id}/teams-test`), 'Test message sent to your Teams');

  const actionFor = (step) => {
    if (step.key === 'install' && setup?.can_download_package) return <Button size="sm" variant="outline" disabled={busy === 'package'} onClick={downloadPackage}><Download size={14} className="mr-1.5" />App package</Button>;
    if (step.key === 'people') return <Button size="sm" variant="outline" onClick={() => onManageLinks(integration)}>Review links</Button>;
    if (step.key === 'test') return <Button size="sm" variant="outline" disabled={busy === 'test'} onClick={sendTest}><Send size={14} className="mr-1.5" />Send test</Button>;
    return null;
  };

  return <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="max-w-lg" data-testid="teams-setup-dialog">
      <DialogHeader>
        <DialogTitle>Set up Microsoft Teams</DialogTitle>
        <DialogDescription>People ask Qflow and use Floor Space from Teams, with the same permissions they have in Qflow.</DialogDescription>
      </DialogHeader>
      {!setup ? <p className="py-6 text-center text-sm text-gray-500">Loading…</p> : <>
        <ol className="divide-y divide-gray-100">
          {setup.steps.map((step, index) => <li key={step.key} className="flex flex-wrap items-center gap-3 py-3">
            <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-semibold ${step.done ? 'qflow-accent-bg' : 'border border-gray-300 text-gray-500'}`} aria-label={step.done ? 'Done' : `Step ${index + 1}`}>{step.done ? <Check size={13} /> : index + 1}</span>
            <div className="min-w-[12rem] flex-1"><p className="text-sm font-medium text-gray-900">{step.label}</p><p className="text-xs text-gray-500">{step.detail}</p></div>
            {actionFor(step)}
          </li>)}
        </ol>
        <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
          <Switch checked={setup.auto_link_by_email} disabled={busy === 'autolink'} onCheckedChange={setAutoLink} />
          <span><span className="font-medium text-gray-900">Link people automatically by work email</span>
            <span className="block text-xs text-gray-500">When someone first messages Qflow in Teams, they're linked to the Qflow account with the same email in this organisation. Guest accounts are never linked automatically.</span></span>
        </label>
      </>}
    </DialogContent>
  </Dialog>;
};

export default TeamsSetupDialog;
